import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import * as client from "../../../client";
import { DataTable } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "primereact/button";
import styles from "./EditSovModal.module.css";
import { useUpdateAddresses } from "../data";
import { isEqual, clone, cloneDeep } from "lodash";
import { applyOverridesToAddress } from "../Conversion";
import { CONSTRUCTION_CODE_DICT } from "./construction";
import { OCCUPANCY_CODE_DICT } from "./occupancy";
import { ThemeContext } from "../../../themes/Theme";
import { colorForConfidence } from "../dashboard/util";
import { AutoComplete } from "primereact/autocomplete";
import { Dropdown } from "primereact/dropdown";
import { featureEnabled } from "../../../client-features";
import { useClientId } from "../../../ClientConfig";
import { editorColumn } from "./DataTableHelpers";

export const CONSTRUCTION_MODAL_INDEX = 0;
export const OCCUPANCY_MODAL_INDEX = 1;

type EditSovModalProps = {
  sovs: client.Sov[];
  lob: string;
  reportId: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  initialActiveIndex: number | undefined;
  rerunningMessage: string | undefined;
};

export const EditSovModal: React.FC<EditSovModalProps> = ({
  sovs,
  lob,
  reportId,
  visible,
  setVisible,
  initialActiveIndex,
  rerunningMessage,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex ?? 0);
  useEffect(() => {
    setActiveIndex(initialActiveIndex ?? 0);
  }, [initialActiveIndex]);
  const [newSovs, setNewSovs] = useState<client.Sov[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // If the input sovs change, set is saving to false. This likely means
  // our edits have finished.
  useEffect(() => {
    if (visible) {
      setIsSaving(false);
      setNewSovs(cloneDeep(sovs));
    }
  }, [sovs, visible]);

  const updateAddresses = useUpdateAddresses(reportId);

  const saveEdits = async () => {
    // Is saving will be set to false automatically once this succeeds
    setIsSaving(true);

    const newAddresses = newSovs.flatMap((sov) => sov.addresses);
    const oldAddresses = new Set(
      sovs
        .flatMap((sov) => sov.addresses)
        .map((address) => JSON.stringify(address))
    );
    const addressesToUpdate = newAddresses.filter(
      (address) => !oldAddresses.has(JSON.stringify(address))
    );
    updateAddresses.mutate({ addresses: addressesToUpdate });
  };

  const sovsAreEqual = useMemo(() => isEqual(sovs, newSovs), [sovs, newSovs]);
  const sovsKey = useMemo(
    () => sovs.map((sov) => sov.sov_id).join("|"),
    [sovs]
  );
  const clientId = useClientId();

  return (
    <Dialog
      header={
        <div className={styles.buttonContainer}>
          <span style={{ marginRight: "16px" }}>Edit Special Fields</span>
          <Button
            label="Save Edits"
            disabled={sovsAreEqual}
            onClick={saveEdits}
            loading={isSaving}
          />
          <Button
            severity="secondary"
            label="Clear Edits"
            disabled={sovsAreEqual}
            onClick={() => setNewSovs(cloneDeep(sovs))}
          />
        </div>
      }
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => (!sovsAreEqual ? undefined : setVisible(false))}
      draggable={false}
      closeIcon={
        <Button
          className={styles.closeButton}
          icon="pi pi-times"
          tooltip={
            !sovsAreEqual ? "Save or clear edits before closing" : "Close"
          }
          tooltipOptions={{
            showOnDisabled: true,
            showDelay: 500,
            position: "left",
          }}
          disabled={!sovsAreEqual}
          text
          rounded
          severity="secondary"
        />
      }
    >
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="Construction">
          <EditConstruction
            key={`construction-${sovsKey}`}
            sovs={newSovs}
            setNewSovs={setNewSovs}
            isSaving={isSaving}
            rerunningMessage={rerunningMessage}
          />
        </TabPanel>
        {featureEnabled(clientId, "show-occupancy", { lob }) && (
          <TabPanel header="Occupancy">
            <EditOccupancy
              key={`occupancy-${sovsKey}`}
              sovs={newSovs}
              setNewSovs={setNewSovs}
              isSaving={isSaving}
              rerunningMessage={rerunningMessage}
            />
          </TabPanel>
        )}
      </TabView>
    </Dialog>
  );
};

type EditProps = {
  sovs: client.Sov[];
  setNewSovs: Dispatch<SetStateAction<client.Sov[]>>;
  isSaving: boolean;
  rerunningMessage: string | undefined;
};

type GroupedAddresses = {
  [key: string]: {
    [key: string]: {
      [key: string]: client.Address[];
    };
  };
};
type CodeCount = {
  code: string;
  codeType: string;
  description: string;
  originalDescription: string;
  rowKey: string;
  count: number;
  confidence: string;
};
const NONE_GIVEN = "NONE GIVEN";
const NONE_FOUND = "NONE FOUND";
const CODE_SPLITTER = "___";
const CONSTRUCTION_CODE_TYPES = ["ISO", "ICM"];

const firstConstructionCodeForType = (codeType: string | undefined | null) => {
  const codeDict = CONSTRUCTION_CODE_DICT[codeType ?? ""];
  if (!codeDict) {
    return "";
  }
  return Object.keys(codeDict)[0] ?? "";
};

const CodeAutocompleteEditor = ({
  options,
  isSaving,
  codeDicts,
  codeType,
  code,
  onCodeChange,
}: {
  options: ColumnEditorOptions;
  isSaving: boolean;
  codeDicts: typeof CONSTRUCTION_CODE_DICT;
  codeType: string;
  code: string;
  onCodeChange: (nextCode: string) => void;
}) => {
  const [suggestions, setSuggestions] = useState<
    { description: string; code: string }[]
  >([]);

  const allOptions = useMemo(() => {
    const codeDict = codeDicts?.[codeType];
    if (!codeDict) {
      return [];
    }
    return Object.entries(codeDict).map(([nextCode, description]) => ({
      description: `${nextCode} - ${description}`,
      code: nextCode,
    }));
  }, [codeDicts, codeType]);

  const updateSuggestions = (query: string) => {
    setSuggestions(
      allOptions.filter(({ code: optionCode }) => optionCode.includes(query))
    );
  };

  const codeDict = codeDicts?.[codeType];
  const displayValue =
    code && codeDict?.[code] ? `${code} - ${codeDict[code]}` : code;

  return (
    <AutoComplete
      key={codeType}
      disabled={isSaving}
      type="text"
      scrollHeight="500px"
      value={displayValue}
      onChange={(e) => {
        const val = e.target.value;
        const nextCode = typeof val === "string" ? val : val.code;
        onCodeChange(nextCode);
        options.editorCallback?.(nextCode);
      }}
      onFocus={() => {
        updateSuggestions("");
      }}
      field="description"
      dropdownMode="blank"
      completeMethod={(e) => updateSuggestions(e.query)}
      suggestions={suggestions}
      dropdown
      inputClassName={styles.autocompleteInput}
    />
  );
};

function sortByConfidencePriority(
  items: CodeCount[],
  confidencePriority = ["user edited", "low", "medium", "high"]
) {
  return items.sort((a, b) => {
    // Check confidence priority first
    const aIndex = confidencePriority.indexOf(a.confidence);
    const bIndex = confidencePriority.indexOf(b.confidence);

    // If both items have known confidence values
    if (aIndex !== -1 && bIndex !== -1) {
      // If they have different confidence values, sort by priority
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
    }

    // If confidence is the same or not in priority list, fall back to count
    return b.count - a.count;
  });
}

export const originalOccupancyDescriptionFromRawAddress = (
  rawAddress: client.Address
) => {
  return (
    rawAddress.private_original_values["occupancy_description"] ??
    rawAddress.private_original_values["location_description"] ??
    rawAddress.private_original_values["location_name"] ??
    NONE_GIVEN
  );
};

const occupancyDescriptionFromCode = (
  codeType: string | undefined,
  code: string | undefined
) => {
  return OCCUPANCY_CODE_DICT[codeType ?? ""]?.[code ?? ""] ?? NONE_FOUND;
};

const firstOccupancyCodeForType = (codeType: string | undefined | null) => {
  const codeDict = OCCUPANCY_CODE_DICT[codeType ?? ""];
  if (!codeDict) {
    return "";
  }
  return Object.keys(codeDict)[0] ?? "";
};

const Confidence = ({ data }: { data: CodeCount }) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const capitalizedConfidence =
    data.confidence.charAt(0).toUpperCase() + data.confidence.slice(1);

  return (
    <div style={{ display: "flex" }}>
      <div
        style={{
          padding: "2px 4px",
          display: "flex",
          backgroundColor: colorForConfidence(data.confidence, isLightMode),
        }}
      >
        <span>{capitalizedConfidence}</span>
      </div>
    </div>
  );
};

export const EditOccupancy: React.FC<EditProps> = ({
  sovs,
  setNewSovs,
  isSaving,
  rerunningMessage,
}) => {
  const [drafts, setDrafts] = useState<
    Record<string, { codeType: string; code: string }>
  >({});
  const groupedOccupancies: GroupedAddresses = {};
  for (const sov of sovs) {
    if (sov.is_enabled) {
      for (const rawAddress of sov.addresses) {
        const address = applyOverridesToAddress(rawAddress);
        if (address.occupancy_code && address.occupancy_code_type) {
          const occCode =
            address.occupancy_code +
            CODE_SPLITTER +
            address.occupancy_code_type;
          if (!(occCode in groupedOccupancies)) {
            groupedOccupancies[occCode] = {};
          }
          const originalDescription =
            originalOccupancyDescriptionFromRawAddress(rawAddress);
          const occDescription = occupancyDescriptionFromCode(
            address.occupancy_code_type,
            address.occupancy_code
          );
          if (!(occDescription in groupedOccupancies[occCode])) {
            groupedOccupancies[occCode][occDescription] = {};
          }
          if (
            !(
              originalDescription in groupedOccupancies[occCode][occDescription]
            )
          ) {
            groupedOccupancies[occCode][occDescription][originalDescription] =
              [];
          }
          groupedOccupancies[occCode][occDescription][originalDescription].push(
            rawAddress
          );
        }
      }
    }
  }

  const occupancyCounts: CodeCount[] = [];
  for (const code of Object.keys(groupedOccupancies)) {
    for (const description of Object.keys(groupedOccupancies[code])) {
      for (const originalDescription of Object.keys(
        groupedOccupancies[code][description]
      )) {
        const addresses =
          groupedOccupancies[code][description][originalDescription];
        const firstAddress = addresses[0];
        const occupancyFlags =
          firstAddress.private_flags["occupancy_code"] ?? [];
        let confidence = "high";
        if (firstAddress.private_overrides["occupancy_code"]) {
          confidence = "useredited";
        } else if (occupancyFlags.includes("low_confidence")) {
          confidence = "low";
        } else if (occupancyFlags.includes("medium_confidence")) {
          confidence = "medium";
        }
        const codeParts = code.split(CODE_SPLITTER);
        const occupancyCount: CodeCount = {
          code: codeParts[0],
          codeType: codeParts[1],
          description: description,
          originalDescription: originalDescription,
          rowKey: `${codeParts[0]}${CODE_SPLITTER}${codeParts[1]}${CODE_SPLITTER}${originalDescription}`,
          count: addresses.length,
          confidence: confidence,
        };
        occupancyCounts.push(occupancyCount);
      }
    }
  }
  sortByConfidencePriority(occupancyCounts);

  const getDraft = (rowData: CodeCount) => {
    return (
      drafts[rowData.rowKey] ?? {
        codeType: rowData.codeType,
        code: rowData.code,
      }
    );
  };

  return (
    <DataTable
      paginator
      rows={30}
      value={occupancyCounts}
      editMode="row"
      onRowEditComplete={(e) => {
        // If there were no edits, exit early
        if (JSON.stringify(e.data) === JSON.stringify(e.newData)) {
          return;
        }
        // Prevent saving the empty string, which can have a bad effect
        if (e.newData.code === "" || e.newData.codeType === "") {
          return;
        }

        const rowKey = e.newData.rowKey;
        const draft = drafts[rowKey];
        if (draft) {
          e.newData.codeType = draft.codeType;
          e.newData.code = draft.code;
        }

        const codeTypeDict =
          OCCUPANCY_CODE_DICT[e.newData.codeType ?? ""] ?? {};
        if (!codeTypeDict[e.newData.code]) {
          const defaultCode = firstOccupancyCodeForType(e.newData.codeType);
          if (defaultCode) {
            e.newData.code = defaultCode;
          }
        }

        setNewSovs((sovs) => {
          const newSovs = [];
          for (const sov of sovs) {
            const newSov = clone(sov);
            for (const rawAddress of newSov.addresses) {
              const address = applyOverridesToAddress(rawAddress);
              if (
                address.occupancy_code_type === e.data.codeType &&
                address.occupancy_code === e.data.code &&
                originalOccupancyDescriptionFromRawAddress(rawAddress) ===
                  e.data.originalDescription
              ) {
                rawAddress.private_overrides.occupancy_code = e.newData.code;
                rawAddress.private_overrides.occupancy_code_type =
                  e.newData.codeType;

                const newDescription = occupancyDescriptionFromCode(
                  e.newData.codeType,
                  e.newData.code
                );
                rawAddress.private_overrides.occupancy_description =
                  newDescription;
              }
            }
            newSovs.push(newSov);
          }
          return newSovs;
        });
      }}
    >
      <Column field="originalDescription" header="Input Description" />
      <Column
        field="code"
        header="Occupancy Code"
        headerStyle={{ minWidth: "200px" }}
        editor={(options) => (
          <CodeAutocompleteEditor
            options={options}
            isSaving={isSaving}
            codeDicts={OCCUPANCY_CODE_DICT}
            codeType={getDraft(options.rowData).codeType}
            code={getDraft(options.rowData).code}
            onCodeChange={(nextCode) =>
              setDrafts((prev) => {
                const rowData = options.rowData;
                const current = prev[rowData.rowKey] ?? {
                  codeType: rowData.codeType,
                  code: rowData.code,
                };
                return {
                  ...prev,
                  [rowData.rowKey]: {
                    codeType: current.codeType,
                    code: nextCode,
                  },
                };
              })
            }
          />
        )}
      />
      <Column
        field="codeType"
        header="Code Type"
        editor={(options) => (
          <Dropdown
            disabled={isSaving}
            value={getDraft(options.rowData).codeType}
            options={Object.keys(OCCUPANCY_CODE_DICT)}
            onChange={(e) => {
              const nextType = e.value;
              const rowData = options.rowData;
              const nextCode =
                firstOccupancyCodeForType(nextType) ?? rowData.code;
              setDrafts((prev) => ({
                ...prev,
                [rowData.rowKey]: {
                  codeType: nextType,
                  code: nextCode,
                },
              }));
              rowData.codeType = nextType;
              rowData.code = nextCode;
              rowData.description = occupancyDescriptionFromCode(
                nextType,
                nextCode
              );
              options.editorCallback?.(nextType);
            }}
          />
        )}
      />
      <Column
        field="description"
        header="Occupancy Code Description"
        editor={(options) => (
          <span style={{ opacity: "0.5" }}>{options.value}</span>
        )}
      />
      <Column
        field="confidence"
        header="Confidence"
        body={(data: CodeCount) => <Confidence data={data} />}
      />
      <Column field="count" header="Count" />
      {editorColumn({
        enabled: !rerunningMessage,
        headerStyle: { width: "10%", minWidth: "8rem" },
        disabledMessage: rerunningMessage,
      })}
    </DataTable>
  );
};

export const originalConstructionDescriptionFromRawAddress = (
  rawAddress: client.Address
) => {
  return (
    rawAddress.private_original_values["construction_description"] ?? NONE_GIVEN
  );
};

const constructionDescriptionFromCode = (
  codeType: string | undefined | null,
  code: string | undefined | null
) => {
  return CONSTRUCTION_CODE_DICT[codeType ?? ""]?.[code ?? ""] ?? NONE_FOUND;
};

const EditConstruction: React.FC<EditProps> = ({
  sovs,
  setNewSovs,
  isSaving,
  rerunningMessage,
}) => {
  const [drafts, setDrafts] = useState<
    Record<string, { codeType: string; code: string }>
  >({});
  const groupedConstructions: GroupedAddresses = {};
  for (const sov of sovs) {
    if (sov.is_enabled) {
      for (const rawAddress of sov.addresses) {
        const address = applyOverridesToAddress(rawAddress);

        const conCode =
          (address.construction_code ?? NONE_FOUND) +
          CODE_SPLITTER +
          (address.construction_code_type ?? NONE_FOUND);
        if (!(conCode in groupedConstructions)) {
          groupedConstructions[conCode] = {};
        }

        const conDescription = constructionDescriptionFromCode(
          address.construction_code_type,
          address.construction_code
        );
        const originalDescription =
          originalConstructionDescriptionFromRawAddress(rawAddress);
        if (!(conDescription in groupedConstructions[conCode])) {
          groupedConstructions[conCode][conDescription] = {};
        }
        if (
          !(
            originalDescription in groupedConstructions[conCode][conDescription]
          )
        ) {
          groupedConstructions[conCode][conDescription][originalDescription] =
            [];
        }

        groupedConstructions[conCode][conDescription][originalDescription].push(
          rawAddress
        );
      }
    }
  }

  const constructionCounts: CodeCount[] = [];
  for (const code of Object.keys(groupedConstructions)) {
    for (const description of Object.keys(groupedConstructions[code])) {
      for (const originalDescription of Object.keys(
        groupedConstructions[code][description]
      )) {
        const addresses =
          groupedConstructions[code][description][originalDescription];
        const firstAddress = addresses[0];
        const constructionFlags =
          firstAddress.private_flags["construction_code"] ?? [];
        let confidence = "high";
        if (firstAddress.private_overrides["construction_code"]) {
          confidence = "user edited";
        } else if (constructionFlags.includes("low_confidence")) {
          confidence = "low";
        } else if (constructionFlags.includes("medium_confidence")) {
          confidence = "medium";
        }

        const codeParts = code.split(CODE_SPLITTER);
        const constructionCount: CodeCount = {
          code: codeParts[0],
          codeType: codeParts[1],
          description: description,
          originalDescription: originalDescription,
          rowKey: `${codeParts[0]}${CODE_SPLITTER}${codeParts[1]}${CODE_SPLITTER}${originalDescription}`,
          count: addresses.length,
          confidence: confidence,
        };
        constructionCounts.push(constructionCount);
      }
    }
  }
  sortByConfidencePriority(constructionCounts);

  const getDraft = (rowData: CodeCount) => {
    return (
      drafts[rowData.rowKey] ?? {
        codeType: rowData.codeType,
        code: rowData.code,
      }
    );
  };

  return (
    <DataTable
      paginator
      rows={30}
      value={constructionCounts}
      editMode="row"
      onRowEditComplete={(e) => {
        // If there were no edits, exit early
        if (JSON.stringify(e.data) === JSON.stringify(e.newData)) {
          return;
        }
        // Prevent saving the empty string, which can have a bad effect
        if (e.newData.code === "" || e.newData.codeType === "") {
          return;
        }

        const rowKey = e.newData.rowKey;
        const draft = drafts[rowKey];
        if (draft) {
          e.newData.codeType = draft.codeType;
          e.newData.code = draft.code;
        }

        const codeTypeDict =
          CONSTRUCTION_CODE_DICT[e.newData.codeType ?? ""] ?? {};
        if (!codeTypeDict[e.newData.code]) {
          const defaultCode = firstConstructionCodeForType(e.newData.codeType);
          if (defaultCode) {
            e.newData.code = defaultCode;
          }
        }

        setNewSovs((sovs) => {
          const newSovs = [];
          for (const sov of sovs) {
            const newSov = clone(sov);
            for (const rawAddress of newSov.addresses) {
              const address = applyOverridesToAddress(rawAddress);
              if (
                (address.construction_code_type ?? NONE_FOUND) ===
                  e.data.codeType &&
                (address.construction_code ?? NONE_FOUND) === e.data.code &&
                originalConstructionDescriptionFromRawAddress(rawAddress) ===
                  e.data.originalDescription
              ) {
                rawAddress.private_overrides.construction_code = e.newData.code;
                rawAddress.private_overrides.construction_code_type =
                  e.newData.codeType;

                const newDescription = constructionDescriptionFromCode(
                  e.newData.codeType,
                  e.newData.code
                );
                rawAddress.private_overrides.construction_description =
                  newDescription;
              }
            }
            newSovs.push(newSov);
          }
          return newSovs;
        });
      }}
    >
      <Column field="originalDescription" header="Input Description" />
      <Column
        field="code"
        header="Construction Code"
        headerStyle={{ minWidth: "200px" }}
        editor={(options) => (
          <CodeAutocompleteEditor
            options={options}
            isSaving={isSaving}
            codeDicts={CONSTRUCTION_CODE_DICT}
            codeType={getDraft(options.rowData).codeType}
            code={getDraft(options.rowData).code}
            onCodeChange={(nextCode) =>
              setDrafts((prev) => {
                const rowData = options.rowData;
                const current = prev[rowData.rowKey] ?? {
                  codeType: rowData.codeType,
                  code: rowData.code,
                };
                return {
                  ...prev,
                  [rowData.rowKey]: {
                    codeType: current.codeType,
                    code: nextCode,
                  },
                };
              })
            }
          />
        )}
      />
      <Column
        field="codeType"
        header="Code Type"
        editor={(options) => (
          <Dropdown
            disabled={isSaving}
            value={getDraft(options.rowData).codeType}
            options={[...CONSTRUCTION_CODE_TYPES]}
            onChange={(e) => {
              const nextType = e.value;
              const rowData = options.rowData;
              const nextCode =
                firstConstructionCodeForType(nextType) ?? rowData.code;
              setDrafts((prev) => ({
                ...prev,
                [rowData.rowKey]: {
                  codeType: nextType,
                  code: nextCode,
                },
              }));
              rowData.codeType = nextType;
              rowData.code = nextCode;
              rowData.description = constructionDescriptionFromCode(
                nextType,
                nextCode
              );
              options.editorCallback?.(nextType);
            }}
          />
        )}
      />
      <Column
        field="description"
        header="Construction Code Description"
        editor={(options) => (
          <span style={{ opacity: "0.5" }}>{options.value}</span>
        )}
      />
      <Column
        field="confidence"
        header="Confidence"
        body={(data: CodeCount) => <Confidence data={data} />}
      />
      <Column field="count" header="Count" />
      {editorColumn({
        enabled: !rerunningMessage,
        headerStyle: { width: "10%", minWidth: "8rem" },
        disabledMessage: rerunningMessage,
      })}
    </DataTable>
  );
};
